/**
 * Per-table preferences that belong to the operator, not to the link.
 *
 * Filters live in the URL because they are what you share. Density and which
 * columns you hid are the opposite: personal, and actively unhelpful to force
 * on whoever you paste the link to. So these persist per table in
 * localStorage and never touch the query string.
 */
import { useCallback, useEffect, useState } from 'react';

export type Density = 'compact' | 'cozy' | 'comfortable';

export type TablePrefs = {
  density: Density;
  /** Column id → visible. Absent ids fall back to the column's own default. */
  columns: Record<string, boolean>;
};

const KEY_PREFIX = 'pm-admin-table:';

function read(key: string): Partial<TablePrefs> {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<TablePrefs>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // A corrupt or quota-blocked store must not take the table down with it.
    return {};
  }
}

function write(key: string, prefs: TablePrefs): void {
  try {
    localStorage.setItem(KEY_PREFIX + key, JSON.stringify(prefs));
  } catch {
    /* private browsing / quota — preferences just don't persist */
  }
}

export function use_table_prefs(
  key: string,
  defaults: { density?: Density; columns?: Record<string, boolean> } = {},
) {
  const [prefs, set_prefs] = useState<TablePrefs>(() => {
    const stored = read(key);
    return {
      density: stored.density ?? defaults.density ?? 'cozy',
      columns: { ...(defaults.columns ?? {}), ...(stored.columns ?? {}) },
    };
  });

  useEffect(() => {
    write(key, prefs);
  }, [key, prefs]);

  const set_density = useCallback((density: Density) => {
    set_prefs((p) => ({ ...p, density }));
  }, []);

  const set_column_visible = useCallback((id: string, visible: boolean) => {
    set_prefs((p) => ({ ...p, columns: { ...p.columns, [id]: visible } }));
  }, []);

  const set_columns = useCallback((columns: Record<string, boolean>) => {
    set_prefs((p) => ({ ...p, columns }));
  }, []);

  const reset_columns = useCallback(() => {
    set_prefs((p) => ({ ...p, columns: { ...(defaults.columns ?? {}) } }));
    // defaults.columns is a literal at every call site, so depending on it by
    // identity would reset on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { prefs, set_density, set_column_visible, set_columns, reset_columns };
}
